import { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Search, X, Bell, CheckSquare, FileText } from 'lucide-react';
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover';
import axios from 'axios';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const GlobalSearch = () => {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [todoSections, setTodoSections] = useState([]);
  const [quickReminder, setQuickReminder] = useState({ dealId: null, note: '', option: '', customDate: '' });
  const [quickTask, setQuickTask] = useState({ dealId: null, text: '', section: '' });
  const [quickNote, setQuickNote] = useState({ dealId: null, content: '' });
  const [activePopover, setActivePopover] = useState({ type: null, dealId: null });
  const navigate = useNavigate();
  const location = useLocation();
  const inputRef = useRef(null);
  const wrapperRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        // Don't close if clicking inside a Radix popover portal
        if (e.target.closest('[data-radix-popper-content-wrapper]') || e.target.closest('[role="dialog"]')) return;
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    if (!query.trim()) { setResults([]); return; }
    const timer = setTimeout(async () => {
      setLoading(true);
      try {
        const { data } = await axios.get(`${API_URL}/api/accounts/search`, { params: { q: query }, withCredentials: true });
        setResults(data.slice(0, 8));
      } catch { setResults([]); }
      setLoading(false);
    }, 250);
    return () => clearTimeout(timer);
  }, [query]);

  useEffect(() => {
    const handleKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') { e.preventDefault(); inputRef.current?.focus(); setOpen(true); }
      if (e.key === 'Escape') { setOpen(false); inputRef.current?.blur(); }
    };
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, []);

  // Fetch todo sections once
  useEffect(() => {
    axios.get(`${API_URL}/api/todo-sections`, { withCredentials: true })
      .then(({ data }) => setTodoSections(data))
      .catch(() => {});
  }, []);

  if (location.pathname === '/all-deals') return null;

  const handleQuickReminder = async (dealId) => {
    let remindAt;
    const now = new Date();
    switch (quickReminder.option) {
      case 'today': remindAt = new Date(Date.UTC(now.getFullYear(), now.getMonth(), now.getDate(), 5, 1)); break;
      case 'tomorrow': { const t = new Date(now); t.setDate(t.getDate() + 1); remindAt = new Date(Date.UTC(t.getFullYear(), t.getMonth(), t.getDate(), 5, 1)); break; }
      case 'next_week': { const t = new Date(now); t.setDate(t.getDate() + 7); remindAt = new Date(Date.UTC(t.getFullYear(), t.getMonth(), t.getDate(), 5, 1)); break; }
      case 'custom': if (quickReminder.customDate) { const [y, m, d] = quickReminder.customDate.split('-'); remindAt = new Date(Date.UTC(parseInt(y), parseInt(m) - 1, parseInt(d), 5, 1)); } break;
      default: break;
    }
    if (!remindAt) { toast.error('Pick a date'); return; }
    try {
      await axios.post(`${API_URL}/api/accounts/${dealId}/reminders`, { note: quickReminder.note || 'Follow up', remind_at: remindAt.toISOString() }, { withCredentials: true });
      toast.success('Reminder set!');
      setActivePopover({ type: null, dealId: null });
    } catch { toast.error('Failed'); }
  };

  const handleQuickTask = async (dealId) => {
    if (!quickTask.text.trim()) { toast.error('Enter a task'); return; }
    const sectionName = quickTask.section || (todoSections[0]?.section) || 'General';
    try {
      // Fetch fresh content right before saving to avoid overwriting
      const { data: freshSections } = await axios.get(`${API_URL}/api/todo-sections`, { withCredentials: true });
      const existing = freshSections.find(s => s.section === sectionName);
      const dealName = results.find(r => r.id === dealId)?.account_name || '';
      const taskLabel = dealName ? `${quickTask.text} [${dealName}]` : quickTask.text;
      const newItem = `<li data-type="taskItem" data-checked="false"><label><input type="checkbox"><span></span></label><div><p>${taskLabel}</p></div></li>`;
      let newContent;
      if (existing?.content?.includes('data-type="taskList"')) {
        newContent = existing.content.replace(/(<ul[^>]*data-type="taskList"[^>]*>)/, `$1${newItem}`);
      } else {
        newContent = `<ul data-type="taskList">${newItem}</ul>`;
      }
      await axios.put(`${API_URL}/api/todo-sections/${encodeURIComponent(sectionName)}`, { content: newContent }, { withCredentials: true });
      setTodoSections(freshSections.map(s => s.section === sectionName ? { ...s, content: newContent } : s));
      toast.success('Task added!');
      setActivePopover({ type: null, dealId: null });
    } catch { toast.error('Failed'); }
  };

  const handleQuickNote = async (dealId) => {
    if (!quickNote.content.trim()) { toast.error('Enter a note'); return; }
    try {
      await axios.post(`${API_URL}/api/accounts/${dealId}/feed`, { content: `<p>${quickNote.content}</p>` }, { withCredentials: true });
      toast.success('Note added!');
      setActivePopover({ type: null, dealId: null });
    } catch { toast.error('Failed'); }
  };

  return (
    <div ref={wrapperRef} className="relative w-full max-w-md" data-testid="global-search">
      <div className="relative">
        <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
        <input
          ref={inputRef}
          data-testid="global-search-input"
          type="text"
          value={query}
          onChange={(e) => { setQuery(e.target.value); setOpen(true); }}
          onFocus={() => setOpen(true)}
          placeholder="Search deals... (⌘K)"
          className="w-full pl-9 pr-8 py-2 text-sm rounded-lg border border-slate-200 bg-slate-50 focus:bg-white focus:border-indigo-300 focus:ring-1 focus:ring-indigo-200 outline-none transition-all placeholder:text-slate-400"
        />
        {query && (
          <button onClick={() => { setQuery(''); setResults([]); }} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600">
            <X size={14} />
          </button>
        )}
      </div>

      {open && query.trim() && (
        <div className="absolute top-full left-0 right-0 mt-1 bg-white rounded-xl border border-slate-200 shadow-lg z-50 overflow-hidden max-h-80 overflow-y-auto">
          {loading ? (
            <div className="px-4 py-3 text-xs text-slate-400">Searching...</div>
          ) : results.length === 0 ? (
            <div className="px-4 py-3 text-xs text-slate-400">No deals found</div>
          ) : (
            results.map(r => (
              <div
                key={r.id}
                data-testid={`search-result-${r.id}`}
                className="px-4 py-2.5 hover:bg-slate-50 transition-colors border-b border-slate-50 last:border-0 flex items-center gap-3 group"
              >
                <div className="flex-1 min-w-0 cursor-pointer" onClick={() => { navigate(`/account/${r.id}`); setQuery(''); setOpen(false); }}>
                  <p className="text-sm font-semibold text-slate-900 truncate">{r.account_name}</p>
                  <p className="text-xs text-slate-500">{r.stage_name} · ${(r.arr || 0).toLocaleString()}</p>
                </div>
                {/* Quick Actions */}
                <div className="flex items-center gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity" onMouseDown={(e) => e.stopPropagation()}>
                  <Popover open={activePopover.type === 'reminder' && activePopover.dealId === r.id}
                    onOpenChange={(o) => { if (o) { setActivePopover({ type: 'reminder', dealId: r.id }); setQuickReminder({ dealId: r.id, note: '', option: '', customDate: '' }); } else setActivePopover({ type: null, dealId: null }); }}>
                    <PopoverTrigger asChild>
                      <button onClick={(e) => e.stopPropagation()} className="p-1 rounded hover:bg-amber-100 text-slate-400 hover:text-amber-600" title="Reminder"><Bell size={13} /></button>
                    </PopoverTrigger>
                    <PopoverContent className="w-60 p-3 rounded-xl" align="end" onClick={(e) => e.stopPropagation()}>
                      <p className="text-xs font-bold text-slate-900 mb-2">Quick Reminder</p>
                      <input value={quickReminder.note} onChange={(e) => setQuickReminder({...quickReminder, note: e.target.value})} placeholder={`Follow up on ${r.account_name}`} className="w-full px-2 py-1.5 border border-slate-200 rounded-lg text-xs mb-2 focus:outline-none focus:border-indigo-500" />
                      <div className="flex flex-wrap gap-1 mb-2">
                        {[['today','Today'],['tomorrow','Tomorrow'],['next_week','Next Week'],['custom','Pick Date']].map(([v,l]) => (
                          <button key={v} onClick={() => setQuickReminder({...quickReminder, option: v})} className={`px-2 py-1 rounded text-[10px] font-semibold ${quickReminder.option === v ? 'bg-indigo-600 text-white' : 'bg-slate-100 text-slate-600'}`}>{l}</button>
                        ))}
                      </div>
                      {quickReminder.option === 'custom' && <input type="date" value={quickReminder.customDate} onChange={(e) => setQuickReminder({...quickReminder, customDate: e.target.value})} className="w-full px-2 py-1.5 border border-slate-200 rounded-lg text-xs mb-2" />}
                      <button onClick={() => handleQuickReminder(r.id)} className="w-full bg-indigo-600 text-white rounded-lg py-1.5 text-xs font-semibold hover:bg-indigo-700">Set</button>
                    </PopoverContent>
                  </Popover>
                  <Popover open={activePopover.type === 'task' && activePopover.dealId === r.id}
                    onOpenChange={(o) => { if (o) { setActivePopover({ type: 'task', dealId: r.id }); setQuickTask({ dealId: r.id, text: '', section: todoSections[0]?.section || '' }); } else setActivePopover({ type: null, dealId: null }); }}>
                    <PopoverTrigger asChild>
                      <button onClick={(e) => e.stopPropagation()} className="p-1 rounded hover:bg-indigo-100 text-slate-400 hover:text-indigo-600" title="Task"><CheckSquare size={13} /></button>
                    </PopoverTrigger>
                    <PopoverContent className="w-60 p-3 rounded-xl" align="end" onClick={(e) => e.stopPropagation()}>
                      <p className="text-xs font-bold text-slate-900 mb-2">Quick Task</p>
                      <input value={quickTask.text} onChange={(e) => setQuickTask({...quickTask, text: e.target.value})} placeholder="Task..." className="w-full px-2 py-1.5 border border-slate-200 rounded-lg text-xs mb-2 focus:outline-none focus:border-indigo-500" />
                      {todoSections.length > 0 && <select value={quickTask.section} onChange={(e) => setQuickTask({...quickTask, section: e.target.value})} className="w-full px-2 py-1.5 border border-slate-200 rounded-lg text-xs mb-2">{todoSections.map(s => <option key={s.section} value={s.section}>{s.section}</option>)}</select>}
                      <button onClick={() => handleQuickTask(r.id)} className="w-full bg-indigo-600 text-white rounded-lg py-1.5 text-xs font-semibold hover:bg-indigo-700">Add</button>
                    </PopoverContent>
                  </Popover>
                  <Popover open={activePopover.type === 'note' && activePopover.dealId === r.id}
                    onOpenChange={(o) => { if (o) { setActivePopover({ type: 'note', dealId: r.id }); setQuickNote({ dealId: r.id, content: '' }); } else setActivePopover({ type: null, dealId: null }); }}>
                    <PopoverTrigger asChild>
                      <button onClick={(e) => e.stopPropagation()} className="p-1 rounded hover:bg-emerald-100 text-slate-400 hover:text-emerald-600" title="Note"><FileText size={13} /></button>
                    </PopoverTrigger>
                    <PopoverContent className="w-60 p-3 rounded-xl" align="end" onClick={(e) => e.stopPropagation()}>
                      <p className="text-xs font-bold text-slate-900 mb-2">Quick Note</p>
                      <textarea value={quickNote.content} onChange={(e) => setQuickNote({...quickNote, content: e.target.value})} placeholder="Note..." className="w-full px-2 py-1.5 border border-slate-200 rounded-lg text-xs mb-2 h-16 resize-none focus:outline-none focus:border-indigo-500" />
                      <button onClick={() => handleQuickNote(r.id)} className="w-full bg-indigo-600 text-white rounded-lg py-1.5 text-xs font-semibold hover:bg-indigo-700">Save</button>
                    </PopoverContent>
                  </Popover>
                </div>
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
};
